/**
 * StructuralErrors — why the Metro 2 structural pre-check refused a Path B
 * file (sheet 12 callout 10; mission 6 WP07 T043; FR-010; U.4, ANA.13, B.3;
 * contracts/ui-api.md "Rejected (sheet 12)").
 *
 * `{code, detail, offset}` rows in the API's order: the code and the offset in
 * mono, `detail` verbatim (the code's constant sentence from the API; no
 * UI-side code-to-sentence map), a null offset as the empty marker.
 * At most LIST_CAP (200) rows render (NFR-005; mission 6 review follow-up 16,
 * A-3). Whenever fewer render than exist (`errors_truncated`, the cap, or
 * both) the caption states that only the first errors are listed, with the
 * shown and total counts; Path B has no error report to point at. `errors: null` renders nothing here: the parent
 * keeps the count, the header and the statement.
 */
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { StructuralError } from '@/lib/api/runs'
import { Mono, count } from '@/lib/format'
import { copyText } from '@/lib/provisional-copy'
import { LIST_CAP } from '@/lib/queries'

/** The member names of the contract's `{code, detail, offset}`, as column headers. */
const HEADS = ['Code', 'Detail', 'Offset'] as const

export interface StructuralErrorsProps {
  rejection: {
    error_count: number
    /** The contract allows null once the rows are gone (history_handlers.go). */
    errors: StructuralError[] | null
    errors_truncated: boolean
  }
}

export function StructuralErrors({ rejection }: StructuralErrorsProps) {
  const { errors, error_count, errors_truncated } = rejection
  if (errors === null) return null
  const rows = errors.slice(0, LIST_CAP)
  const total = Math.max(error_count, errors.length)
  return (
    <Table>
      {errors_truncated || rows.length < total ? (
        <TableCaption>
          {copyText('truncationFooterPathB')} <Mono>{count(rows.length)}</Mono> of <Mono>{count(total)}</Mono>
        </TableCaption>
      ) : null}
      <TableHeader>
        <TableRow>
          {HEADS.map((h) => (
            <TableHead key={h}>{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((e, i) => (
          <TableRow key={`${e.code}:${i}`} dense>
            <TableCell>
              <Mono>{e.code}</Mono>
            </TableCell>
            <TableCell>{e.detail}</TableCell>
            <TableCell numeric>{e.offset === null ? '—' : count(e.offset)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
